package com.essntl.core.unsplash.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Card
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.type
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.essntl.core.unsplash.domain.UnsplashClient
import com.essntl.core.unsplash.domain.models.Picture
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.uikit.components.button.outline.EsLargeOutlineButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.checkbox.EsCheckBox
import com.essntl.uikit.components.checkbox.EsCheckBoxColors
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.iconbutton.secondary.EsLargeSecondaryIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.image.AsyncUrlImage
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.resources.LocalRes
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import org.jetbrains.compose.resources.painterResource
import org.koin.compose.koinInject

@Composable
fun UnsplashPopup(
    isOpen: Boolean,
    onDismissRequest: () -> Unit,
    multipleSelect: Boolean,
    onAddImages: (urls: List<String>, keyword: String) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val unsplashClient = koinInject<UnsplashClient>()
    val loadingManager = koinInject<LoadingManager>()

    val unsplashViewModel = remember(
        scope,
        unsplashClient,
        loadingManager,
    ) {
        UnsplashViewModel(
            scope = scope,
            unsplashClient = unsplashClient,
            loadingManager = loadingManager,
        )
    }

    val state by unsplashViewModel.state.collectAsState()

    if (isOpen) {
        Dialog(
            onDismissRequest = onDismissRequest,
        ) {
            UnsplashPopupUI(
                onDismissRequest = onDismissRequest,
                pictures = state.getAllPicture.data,
                isLoading = state.getAllPicture.isLoading && state.getAllPicture.loadedPages == 0,
                isFailure = state.getAllPicture.isFailure,
                errorMessage = state.getAllPicture.error?.message,
                paginationQuery = state.getAllPicture.query,
                paginationLoading = state.getAllPicture.isLoading,
                paginationLoadMore = !state.getAllPicture.isLastPage,
                onEvent = unsplashViewModel::onEvent,
                multipleSelect = multipleSelect,
                onAddImages = onAddImages,
            )
        }
    }
}

@Composable
fun UnsplashPopupUI(
    onDismissRequest: () -> Unit,
    paginationQuery: String,
    pictures: List<Picture>,
    isLoading: Boolean,
    isFailure: Boolean,
    errorMessage: String?,
    paginationLoading: Boolean,
    paginationLoadMore: Boolean,
    onEvent: (UnsplashEvent) -> Unit,
    multipleSelect: Boolean,
    onAddImages: (urls: List<String>, keyword: String) -> Unit,
) {
    var openedPopup by remember {
        mutableStateOf(true)
    }

    var searchText by remember { mutableStateOf("") }

    val loadMoreEnabled = remember(paginationQuery, searchText, paginationLoadMore) {
        paginationLoadMore && (searchText == paginationQuery)
    }

    val searchEnabled = remember(paginationQuery, searchText, loadMoreEnabled) {
        loadMoreEnabled || (searchText != paginationQuery)
    }

    var selectedPictureIds by remember {
        mutableStateOf(emptySet<String>())
    }

    fun searchPhotos() {
        if (searchText.trim().isNotEmpty()) {
            if (openedPopup) openedPopup = false
            onEvent(UnsplashEvent.LoadMorePicture(searchText))
        }
    }

    Card(
        shape = RoundedCornerShape(25.dp),
        modifier = Modifier.width(500.dp),
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
            modifier = Modifier
                .fillMaxWidth()
                .background(color = Color.White),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(color = MaterialTheme.esColorScheme.bgPrimary)
                    .padding(vertical = 3.spacing, horizontal = 2.5.spacing),
            ) {
                Text(
                    text = "Get photos from unsplash",
                    style = MaterialTheme.esTypography.h3Bold24,
                    color = MaterialTheme.esColorScheme.textPrimary,
                    modifier = Modifier.align(Alignment.CenterStart),
                )

                EsSmallOutlineIconButton(
                    onClick = onDismissRequest,
                    iconRes = EsIcons.Close,
                    modifier = Modifier.align(Alignment.CenterEnd),
                )
            }

            Spacer(Modifier.height(height = 3.5.spacing))

            Row(
                horizontalArrangement = Arrangement.spacedBy(1.5.spacing),
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 2.5.spacing),
            ) {
                EsInput(
                    value = searchText,
                    onValueChange = {
                        searchText = it
                    },
                    placeholder = "Search",
                    shape = RoundedCornerShape(50.dp),
                    modifier = Modifier
                        .width(400.dp)
                        .onPreviewKeyEvent { event ->
                            if (event.key == Key.Enter &&
                                event.type == KeyEventType.KeyUp
                            ) {
                                searchPhotos()
                                true
                            } else {
                                false
                            }
                        },
                )
                EsLargeSecondaryIconButton(
                    iconRes = EsIcons.Search,
                    enabled = searchEnabled && searchText.trim().isNotEmpty(),
                    onClick = {
                        searchPhotos()
                    },
                    modifier = Modifier
                        .clip(CircleShape),
                )
            }

            Spacer(Modifier.height(height = 3.5.spacing))

            val res = LocalRes.current

            LazyVerticalGrid(
                columns = GridCells.Fixed(
                    if (openedPopup)
                        3
                    else if (isLoading || pictures.isEmpty())
                        1
                    else
                        3,
                ),
                horizontalArrangement = Arrangement.spacedBy(1.spacing),
                verticalArrangement = Arrangement.spacedBy(1.5.spacing),
                userScrollEnabled = true,
                modifier = Modifier
                    .padding(horizontal = 2.5.spacing)
                    .heightIn(max = 440.dp),
            ) {
                if (openedPopup) {
                    items(9) {
                        Image(
                            painter = painterResource(res.addImage),
                            contentDescription = "add image",
                        )
                    }
                } else if (isLoading && pictures.isEmpty()) {
                    item {
                        Box(
                            contentAlignment = Alignment.Center,
                            modifier = Modifier.fillMaxSize(),
                        ) {
                            EsSpinner(
                                color = MaterialTheme.esColorScheme.iconPrimary,
                                size = EsSpinnerSize.Large,
                                modifier = Modifier
                                    .align(Alignment.Center),
                            )
                        }
                    }
                } else if (pictures.isEmpty() || isFailure) {
                    item {
                        Box(
                            contentAlignment = Alignment.Center,
                            modifier = Modifier.fillMaxSize(),
                        ) {
                            Text(
                                text = if (isFailure) {
                                    errorMessage ?: "Something goes wrong!"
                                } else "No pictures found!",
                                textAlign = TextAlign.Center,
                                style = MaterialTheme.esTypography.p1Regular16,
                                color = MaterialTheme.esColorScheme.textPrimary,
                                modifier = Modifier
                                    .align(Alignment.Center),
                            )
                        }
                    }
                } else {
                    items(pictures) { picture ->
                        val isSelected = remember(picture.id, selectedPictureIds) {
                            picture.id in selectedPictureIds
                        }

                        Box(
                            modifier = Modifier
                                .weight(1f)
                                .aspectRatio(1f)
                                .fillMaxHeight()
                                .clip(shape = RoundedCornerShape(20.dp)),
                        ) {
                            AsyncUrlImage(
                                url = "${picture.url}?w=500",
                                placeholder = {
                                    Box(
                                        contentAlignment = Alignment.Center,
                                        modifier = Modifier
                                            .fillMaxSize()
                                            .background(
                                                color = MaterialTheme.esColorScheme.bgPrimary,
                                                shape = RoundedCornerShape(20.dp),
                                            ),
                                    ) {
                                        EsSpinner(
                                            color = MaterialTheme.esColorScheme.iconPrimary,
                                            size = EsSpinnerSize.Large,
                                            modifier = Modifier
                                                .align(Alignment.Center),
                                        )
                                    }
                                },
                                modifier = Modifier
                                    .fillMaxSize()
                                    .clickable {
                                        val oldSelectedPictureIds =
                                            if (multipleSelect)
                                                selectedPictureIds
                                            else
                                                emptySet()

                                        selectedPictureIds =
                                            if (isSelected)
                                                oldSelectedPictureIds - picture.id
                                            else
                                                oldSelectedPictureIds + picture.id
                                    },
                                contentScale = ContentScale.Crop,
                            )
                            EsCheckBox(
                                colors = EsCheckBoxColors(
                                    backgroundCheckedBoxColor = MaterialTheme.esColorScheme.secondaryHover,
                                    backgroundUncheckedBoxColor = MaterialTheme.esColorScheme.secondaryHover,
                                    borderCheckedBoxColor = MaterialTheme.esColorScheme.borderAccent,
                                    checkMarkColor = MaterialTheme.esColorScheme.iconAccent,
                                    disabledCheckedBoxColor = MaterialTheme.esColorScheme.secondaryDisabled,
                                    disabledCheckMarkColor = MaterialTheme.esColorScheme.iconDisabled,
                                ),
                                checked = isSelected,
                                onCheckedChange = {
                                    val oldSelectedPictureIds =
                                        if (multipleSelect)
                                            selectedPictureIds
                                        else
                                            emptySet()

                                    selectedPictureIds =
                                        if (isSelected)
                                            oldSelectedPictureIds - picture.id
                                        else
                                            oldSelectedPictureIds + picture.id
                                },
                                modifier = Modifier
                                    .align(Alignment.TopEnd)
                                    .padding(
                                        top = 1.spacing,
                                        end = 1.spacing,
                                    ),
                            )
                        }
                    }
                }
            }

            Spacer(Modifier.height(height = 1.5.spacing))

            // Todo: When the window is small the button is not visible
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(
                        top = 1.5.spacing,
                        bottom = 2.5.spacing,
                        start = 2.5.spacing,
                        end = 2.5.spacing,
                    ),
            ) {
                EsLargePrimaryButton(
                    text = "Save photos",
                    onClick = {
                        onAddImages(
                            pictures.filter { it.id in selectedPictureIds }.map { it.url },
                            searchText,
                        )
                        onDismissRequest()
                    },
                    enabled = selectedPictureIds.isNotEmpty(),
                    modifier = Modifier
                        .weight(1f),
                )

                Spacer(modifier = Modifier.width(2.spacing))

                EsLargeOutlineButton(
                    onClick = {
                        searchPhotos()
                    },
                    enabled = loadMoreEnabled && !openedPopup,
                    text = "Load more",
                    loading = loadMoreEnabled && paginationLoading && !isLoading,
                    modifier = Modifier
                        .weight(1f),
                )
            }
        }
    }
}
